import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { getFeedByIdService, likeFeedService } from "../../../services/feeds";
import { getFeedTypeColor } from "../../../utils/feedUtils";

const getLikesCount = (feed) =>
  feed?.likes_count ?? feed?.reactions?.likes ?? 0;

const getAvatarUrl = (person) =>
  person?.profile_image ||
  person?.profileImage ||
  person?.avatar ||
  person?.image ||
  person?.photo ||
  "";

const getInitials = (person) => {
  const first = person?.first_name?.trim()?.[0] || "";
  const last = person?.last_name?.trim()?.[0] || "";
  const initials = `${first}${last}`.trim();
  return initials || person?.name?.trim()?.[0] || "S";
};

const getFullName = (person) => {
  const first = person?.first_name || "";
  const last = person?.last_name || "";
  const fullName = `${first} ${last}`.trim();
  return fullName || person?.name || "System";
};

const isFeedEdited = (feed) => Boolean(feed?.is_edited);

const FeedDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [feed, setFeed] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLiking, setIsLiking] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    const fetchFeed = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getFeedByIdService(id);
        const item = response?.data || response?.feed || response;

        if (item) {
          setFeed(item);
        } else {
          setError({ type: "NOT_FOUND" });
        }
      } catch (err) {
        console.error("Error fetching feed:", err);
        if (err?.response?.data?.code === "FEED_DELETED") {
          setError({ type: "FEED_DELETED", message: err.response.data.message });
        } else if (!navigator.onLine || err?.message === "Network Error" || err?.code === "ERR_NETWORK") {
          setError({ type: "OFFLINE" });
        } else {
          setError({ type: "GENERIC" });
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchFeed();
    }
  }, [id]);

  const handleLike = async () => {
    if (!feed || feed.visibility_type?.toUpperCase() !== "PUBLIC" || isLiking) {
      return;
    }

    setIsLiking(true);
    const wasLiked = feed.liked_by_current_user;

    try {
      const response = await likeFeedService(feed._id || feed.id);

      const updatedFeed =
        response?.data?.data ||
        response?.data?.feed ||
        response?.data ||
        response?.feed ||
        {};

      const backendLikes =
        updatedFeed?.likes_count ?? updatedFeed?.reactions?.likes;

      const newLikesCount =
        backendLikes !== undefined
          ? backendLikes
          : wasLiked
            ? Math.max(0, getLikesCount(feed) - 1)
            : getLikesCount(feed) + 1;

      setFeed((prev) =>
        prev
          ? {
              ...prev,
              liked_by_current_user: !wasLiked,
              likes_count: newLikesCount,
              reactions: {
                ...(prev.reactions || {}),
                likes: newLikesCount,
              },
            }
          : prev,
      );
    } catch (err) {
      console.error("Error toggling like for feed:", err);
    } finally {
      setIsLiking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-4xl mx-auto mt-4 px-4 pb-12">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden p-8">
          <Skeleton height={24} width={120} className="mb-4" />
          <Skeleton height={36} className="mb-6" />
          <div className="flex items-center gap-3 mb-8">
            <Skeleton circle height={40} width={40} />
            <div>
              <Skeleton height={16} width={150} />
              <Skeleton height={12} width={200} className="mt-1" />
            </div>
          </div>
          <Skeleton count={6} className="mb-2" />
        </div>
      </div>
    );
  }

  if (error || !feed) {
    let title = "Feed not found";
    let messageText = "This feed may have been deleted or is no longer available.";
    let icon = (
      <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 9v.906a2.25 2.25 0 01-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 001.183 1.981l6.478 3.488m8.839 2.51l-4.66-2.51m0 0l-1.023-.55a2.25 2.25 0 00-2.134 0l-1.022.55m0 0l-4.661 2.51m16.5 1.615a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V8.844a2.25 2.25 0 011.183-1.98l7.5-4.04a2.25 2.25 0 012.134 0l7.5 4.04a2.25 2.25 0 011.183 1.98V18" />
      </svg>
    );

    if (error?.type === "FEED_DELETED") {
      title = error.message || "This feed has been deleted";
      messageText = "The content you are looking for is no longer available.";
      icon = (
        <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
        </svg>
      );
    } else if (error?.type === "OFFLINE") {
      title = "You are offline";
      messageText = "Please check your internet connection and try again.";
      icon = (
        <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M8.528 8.528A11.954 11.954 0 003 12.5c2.18 3.51 6.06 5.86 10.42 5.86 1.7 0 3.32-.38 4.79-1.05m2.26-2.26A11.95 11.95 0 0023 12.5c-2.18-3.51-6.06-5.86-10.42-5.86a11.986 11.986 0 00-4.05.7" />
        </svg>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center py-32 text-gray-600">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
          {icon}
        </div>
        <h2 className="text-lg font-bold text-slate-700 mb-2">
          {title}
        </h2>
        <p className="text-sm text-slate-400 mb-6">
          {messageText}
        </p>
        <button
          onClick={() => navigate("/")}
          className="px-5 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const author =
    feed?.created_by ||
    feed?.createdBy ||
    feed?.created_by_user ||
    feed?.sender ||
    {};
  const avatarUrl = getAvatarUrl(author);
  const initials = getInitials(author);
  const authorName = getFullName(author);
  const likesCount = getLikesCount(feed);
  const isPublicFeed = feed.visibility_type?.toUpperCase() === "PUBLIC";
  const alreadyLiked = Boolean(feed.liked_by_current_user);

  return (
    <div className="w-full max-w-4xl mx-auto mt-4 px-4 pb-12">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <span
              className={`text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider ${getFeedTypeColor(feed.feed_type || feed.type || "ANNOUNCEMENT")}`}
            >
              {feed.feed_type || feed.type || "ANNOUNCEMENT"}
            </span>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 overflow-hidden flex items-center justify-center text-indigo-600 font-bold uppercase">
                {avatarUrl && !avatarError ? (
                  <img
                    src={avatarUrl}
                    alt={authorName}
                    className="w-full h-full object-cover"
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <span>{initials}</span>
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {authorName}
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-slate-500">
                    {author?.designation?.name || "Admin"}
                  </p>
                  {isFeedEdited(feed) && (
                    <span className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full text-amber-700 bg-amber-50 border border-amber-100">
                      Edited
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="text-right">
              <p className="text-sm font-semibold text-slate-700">
                {moment(feed.createdAt).format("MMM DD, YYYY")}
              </p>
              <p className="text-xs text-slate-500">
                {moment(feed.createdAt).format("hh:mm A")}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 md:p-8 min-h-[300px]">
          <div className="space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">
                {feed.title || "No Title"}
              </h2>
            </div>

            {feed.message ? (
              <div>
                <p className="text-base text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {feed.message}
                </p>
              </div>
            ) : null}

            {feed.description ? (
              <div>
                <p className="text-base text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {feed.description}
                </p>
              </div>
            ) : null}

            {feed.link ? (
              <div>
                <a
                  href={feed.link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 break-all underline decoration-blue-200"
                >
                  {feed.link}
                </a>
              </div>
            ) : null}
          </div>

      {isPublicFeed && (
        <div className="mt-8 pt-5 border-t border-slate-100 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleLike}
            disabled={isLiking}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              isLiking
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : alreadyLiked
                ? "bg-red-50 text-red-600 hover:bg-red-100"
                : "bg-slate-100 text-slate-700 hover:bg-red-50 hover:text-red-600"
            }`}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill={alreadyLiked ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>

            <span>
              {alreadyLiked ? "Liked" : isLiking ? "Wait..." : "Like"}
            </span>

            <span>({likesCount})</span>
          </button>
        </div>
      )}
    </div>
  </div>
</div>
);
};
export default FeedDetails;
